import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { api, fmtDate } from "@/lib/api";
import { fmtINR, fmtINRCompact } from "@/lib/format";
import { usePrefs } from "@/contexts/PrefsContext";
import { toast } from "sonner";
import {
    Sparkles, ArrowRight, AlertTriangle, CalendarDays, CheckCircle2, Info,
    Clock, RefreshCcw, ExternalLink,
} from "lucide-react";

/**
 * VAULT · Forward View dashboard
 *
 * Answers: "What financial commitments and important dates are coming next?"
 * Deterministic, uses only confirmed records. Never advice.
 */

const WINDOWS = [
    { key: "7",  label: "Next 7 Days",  tone: "amber" },
    { key: "30", label: "Next 30 Days", tone: "blue" },
    { key: "60", label: "Next 60 Days", tone: "indigo" },
    { key: "90", label: "Next 90 Days", tone: "stone" },
];

const TONE = {
    amber:  "from-amber-50 to-white border-amber-200",
    blue:   "from-blue-50 to-white border-blue-200",
    indigo: "from-indigo-50 to-white border-indigo-200",
    stone:  "from-stone-50 to-white border-stone-200",
};

function BucketCard({ w, data, hide, onOpen }) {
    return (
        <div className={`rounded-2xl border p-5 bg-gradient-to-br ${TONE[w.tone]}`}
             data-testid={`forward-bucket-${w.key}`}>
            <div className="flex items-center justify-between mb-3">
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                    {w.label}
                </div>
                <button onClick={onOpen}
                        className="text-[11px] font-semibold text-stone-700 hover:text-stone-900 inline-flex items-center gap-1"
                        data-testid={`forward-open-${w.key}`}>
                    Open <ArrowRight size={11} />
                </button>
            </div>
            <div className="flex items-baseline gap-2">
                <div className="font-display text-4xl leading-none text-stone-900 tabular-nums">
                    {data?.count ?? 0}
                </div>
                <div className="text-xs text-stone-500">events</div>
            </div>
            <div className="text-[11.5px] text-stone-600 mt-3 space-y-1">
                <div>Renewals · <b className="text-stone-900">{data?.renewals ?? 0}</b></div>
                <div>Expiries · <b className="text-stone-900">{data?.expiries ?? 0}</b></div>
                <div>Payments · <b className="text-stone-900">{data?.payments ?? 0}</b></div>
                <div>Reviews · <b className="text-stone-900">{data?.reviews ?? 0}</b></div>
            </div>
            <div className="mt-4 pt-3 border-t border-stone-200/70">
                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                    Known commitments
                </div>
                <div className="text-lg font-display tabular-nums text-stone-900 mt-0.5">
                    {hide ? "•••••" : fmtINRCompact(data?.known_commitments || 0)}
                </div>
                <div className="text-[10px] text-stone-500 mt-1">
                    Based on records currently stored in your VAULT.
                </div>
            </div>
        </div>
    );
}

export default function ForwardView() {
    const navigate = useNavigate();
    const { prefs } = usePrefs();
    const hide = !!prefs?.hide_balance;
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get("/vault-calendar/forward-view");
            setData(r.data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't load forward view");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const openWindow = (key) => navigate(`/calendar?view=next-${key}`);

    return (
        <div className="max-w-6xl mx-auto p-6 md:p-8" data-testid="forward-view">
            <div className="mb-6 flex items-end justify-between flex-wrap gap-3">
                <div>
                    <div className="text-[11px] uppercase tracking-widest text-amber-800 font-semibold flex items-center gap-1.5">
                        <Sparkles size={12} /> Dashboard · Forward View
                    </div>
                    <h1 className="font-display text-4xl sm:text-5xl text-stone-900 leading-none mt-2">
                        What&rsquo;s coming?
                    </h1>
                    <p className="text-sm text-stone-500 mt-3 max-w-2xl">
                        A chronological view of known financial commitments and important dates.
                        Deterministic, based only on records currently stored in your VAULT.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={load} className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-stone-300 text-[13px] font-semibold text-stone-700 hover:border-stone-500"
                            data-testid="forward-refresh">
                        <RefreshCcw size={12} /> Refresh
                    </button>
                    <button onClick={() => navigate("/calendar")}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-stone-900 text-white text-[13px] font-semibold hover:bg-stone-700"
                            data-testid="forward-open-calendar">
                        <CalendarDays size={13} /> Open calendar
                    </button>
                </div>
            </div>

            {/* Buckets */}
            {loading ? (
                <div className="text-center text-sm text-stone-400 py-16">Loading&hellip;</div>
            ) : (
                <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                        {WINDOWS.map(w => (
                            <BucketCard key={w.key} w={w}
                                        data={data?.buckets?.[w.key]}
                                        hide={hide}
                                        onOpen={() => openWindow(w.key)} />
                        ))}
                    </div>

                    {/* Data completeness notice */}
                    {data?.completeness?.has_gaps && (
                        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3"
                             data-testid="forward-completeness-warning">
                            <AlertTriangle className="text-amber-700 shrink-0 mt-0.5" size={16} />
                            <div className="flex-1">
                                <div className="text-[13px] font-semibold text-amber-900">
                                    Your Forward View may be incomplete because some records do not have upcoming dates.
                                </div>
                                <div className="text-[11.5px] text-amber-800 mt-1">
                                    Missing dates in&nbsp;
                                    {data.completeness.missing.map((m, i) => (
                                        <span key={m.module}>
                                            {i > 0 ? ", " : ""}
                                            <Link to={m.route} className="underline font-semibold" data-testid={`forward-fix-${m.module.toLowerCase().replace(/\s+/g,'-')}`}>
                                                {m.module} ({m.count})
                                            </Link>
                                        </span>
                                    ))}.
                                </div>
                                <button onClick={() => data.completeness.missing[0] && navigate(data.completeness.missing[0].route)}
                                        className="mt-2 inline-flex items-center gap-1 text-[12px] font-semibold text-amber-900 hover:text-amber-950"
                                        data-testid="forward-review-missing">
                                    Review missing dates <ArrowRight size={11} />
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Detailed preview per bucket */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {WINDOWS.map(w => {
                            const b = data?.buckets?.[w.key];
                            const first = b?.first_five || [];
                            return (
                                <div key={w.key} className="bg-white border border-stone-200 rounded-xl overflow-hidden"
                                     data-testid={`forward-preview-${w.key}`}>
                                    <div className="px-4 py-2.5 bg-stone-50 border-b border-stone-200 flex items-center justify-between">
                                        <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">
                                            {w.label} · preview
                                        </div>
                                        <button onClick={() => openWindow(w.key)}
                                                className="text-[11px] font-semibold text-stone-700 hover:text-stone-900 inline-flex items-center gap-1">
                                            See all <ExternalLink size={10} />
                                        </button>
                                    </div>
                                    {first.length === 0 ? (
                                        <div className="p-6 text-center text-xs text-stone-400">Nothing in this window.</div>
                                    ) : first.map(e => (
                                        <div key={e.key} className="border-b border-stone-100 last:border-0 px-4 py-2.5 flex items-center gap-3">
                                            <div className="w-12 shrink-0 text-right">
                                                <div className="text-[10px] uppercase tracking-widest text-stone-500 font-semibold">
                                                    {fmtDate(e.date).split(" ")[1]}
                                                </div>
                                                <div className="text-sm font-display leading-none">{fmtDate(e.date).split(" ")[0]}</div>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="text-[10.5px] uppercase tracking-widest text-stone-500 font-semibold">{e.category}</div>
                                                <div className="text-[12.5px] font-semibold text-stone-900 truncate">{e.name}</div>
                                            </div>
                                            {e.amount != null && (
                                                <div className="text-[12px] font-semibold tabular-nums text-stone-900">
                                                    {hide ? "•••••" : fmtINR(e.amount)}
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                    </div>

                    <div className="mt-6 flex items-start gap-2 text-[11px] text-stone-500 max-w-3xl">
                        <Info size={12} className="mt-0.5 shrink-0" />
                        <span>{data?.disclaimer}</span>
                    </div>
                </>
            )}
        </div>
    );
}
